import { useCallback, useRef, useState } from 'react';

interface PageResult<T> {
  list: T[];
  total: number;
}

interface UsePageListOptions<T> {
  startPage?: number;
  size?: number;
  getData: (params: { pageNo: number; size: number }) => Promise<PageResult<T>>;
}

const usePageList = <T = any>({
  startPage = 1,
  size = 10,
  getData,
}: UsePageListOptions<T>) => {
  /** =======================
   * UI 状态（必须响应式）
   * ======================= */
  const [page, setPage] = useState(startPage);
  const [total, setTotal] = useState(0);
  const [dataList, setDataList] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [finished, setFinished] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState('');

  /** =======================
   * 内部控制（不触发 render）
   * ======================= */
  const loadingRef = useRef(false);

  /** =======================
   * 核心请求方法
   * ======================= */
  const loadData = useCallback(
    async (pageNo: number = page, pageSize = size, replace = false) => {
      if (loadingRef.current) return;
      if (finished && !replace) return;

      loadingRef.current = true;
      setLoading(true);
      setIsError(false);
      setError('');

      try {
        const { list, total: totalCount } = await getData({
          pageNo,
          size: pageSize,
        });

        setTotal(totalCount);

        setDataList(prev => {
          const nextList = replace ? list : [...prev, ...list];
          setFinished(nextList.length >= totalCount);
          return nextList;
        });
        setPage(pageNo);
      } catch (e: any) {
        setIsError(true);
        setError(e?.message || '加载失败');
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [finished, getData, size],
  );

  /** =======================
   * 对外操作
   * ======================= */

  /** 首次 / 刷新 */
  const refresh = useCallback(() => {
    setFinished(false);
    return loadData(startPage, size, true);
  }, [loadData, size, startPage]);

  /** 加载下一页 */
  const loadMore = useCallback(() => {
    return loadData(page + 1, size);
  }, [loadData, page, size]);

  /** 重新加载到当前页（如筛选条件变化） */
  const reload = useCallback(() => {
    const pageCount = Math.max(page - startPage + 1, 1);
    setFinished(false);
    return loadData(startPage, size * pageCount, true);
  }, [loadData, page, size, startPage]);

  return {
    /** 分页信息 */
    page,
    total,
    finished,

    /** 数据 */
    dataList,

    /** 状态 */
    loading,
    isError,
    error,

    /** 操作 */
    loadData,
    refresh,
    loadMore,
    reload,
  };
};

export default usePageList;
